import { apiClient } from '../utils/api-client'
import type { Exercise, ExerciseEquipment } from '../types/exercise'
import type { ExerciseTemplatePayload } from '../types/exercise-template'

interface ExercisesResponse {
  data: Array<{ id: string; type: string; attributes: Exercise }>
}

interface ExerciseResponse {
  data: { id: string; type: string; attributes: Exercise }
}

/**
 * A private exercise the user writes themselves. `templates` replaces the whole
 * block set on update — the API has no partial-block semantics on this route.
 */
export interface CustomExercisePayload {
  title: string
  description?: string | null
  exercise_category_id: string
  duration_minutes?: number | null
  equipment?: ExerciseEquipment | null
  templates?: ExerciseTemplatePayload[]
}

interface SearchParams {
  q?: string
  category_id?: string
  /** Narrows the results to the user's own custom exercises. */
  owned?: boolean
}

class ExercisesService {
  async search(params: SearchParams = {}): Promise<Exercise[]> {
    const search = new URLSearchParams()
    if (params.q) search.set('q', params.q)
    if (params.category_id) search.set('category_id', params.category_id)
    if (params.owned) search.set('owned', 'true')
    const query = search.toString() ? `?${search}` : ''
    const response = await apiClient.get(`/api/exercises${query}`, { requireAuth: true })
    const json: ExercisesResponse = await response.json()
    return json.data.map((d) => ({ ...d.attributes, id: d.id }))
  }

  /** The user's own custom exercises, for the "my workouts" list. */
  async listMine(): Promise<Exercise[]> {
    return this.search({ owned: true })
  }

  async show(id: string): Promise<Exercise> {
    const response = await apiClient.get(`/api/exercises/${id}`, { requireAuth: true })
    const json: ExerciseResponse = await response.json()
    return { ...json.data.attributes, id: json.data.id }
  }

  async create(payload: CustomExercisePayload): Promise<Exercise> {
    const response = await apiClient.post('/api/exercises', {
      data: { attributes: payload },
    }, { requireAuth: true })
    const json: ExerciseResponse = await response.json()
    return { ...json.data.attributes, id: json.data.id }
  }

  async update(id: string, payload: CustomExercisePayload): Promise<Exercise> {
    const response = await apiClient.patch(`/api/exercises/${id}`, {
      data: { attributes: payload },
    }, { requireAuth: true })
    const json: ExerciseResponse = await response.json()
    return { ...json.data.attributes, id: json.data.id }
  }

  async destroy(id: string): Promise<void> {
    await apiClient.delete(`/api/exercises/${id}`, { requireAuth: true })
  }
}

export const exercisesService = new ExercisesService()
